/**
 * Food Safety submit feedback helpers (29.37).
 * Keep in sync with app/utils/foodSafetyFeedback.js (pure offline/error copy).
 */

/** Primary CTA label after a failed save (same button re-attempts). */
export const FOOD_SAFETY_SUBMIT_RETRY_LABEL = 'Retry'

/** Default Submit label when the log is ready to send. */
export const FOOD_SAFETY_SUBMIT_LABEL = 'Submit'

/**
 * Quiet label when out-of-range and corrective note still empty (29.53).
 * Button stays tappable — validation unchanged; chrome must not look “ready”.
 */
export const FOOD_SAFETY_SUBMIT_ADD_NOTE_LABEL = 'Add note to submit'

/**
 * Main button when a reading needs corrective action, on screens where the
 * reading saves anyway and the action can follow.
 */
export const FOOD_SAFETY_SUBMIT_WITH_ACTION_LABEL = 'Save with action'

/** Quiet link in the corrective panel for saving with no action chosen. */
export const FOOD_SAFETY_SUBMIT_WITHOUT_ACTION_LABEL = 'Save without action'

/** Mid-round strip lead (29.66) — pair with formatFoodSafetySubmitError body. */
export const FOOD_SAFETY_SUBMIT_ERROR_LEAD = "Couldn't save — draft kept"

const OFFLINE_SUBMIT_MESSAGE =
  "You're offline — draft kept. Retry when you're back online."
const FAILED_SUBMIT_FALLBACK =
  "Couldn't save — draft kept. Tap Retry to try again."

/**
 * Submit CTA label + quiet chrome for fridge / ad-hoc composers (29.53).
 * Retry (29.37) always wins so a failed save still looks actionable.
 * Keep in sync with app/utils/foodSafetyFeedback.js.
 *
 * With `actionOptional`, saving with an action is the main button; while
 * none is chosen it points at the options (`needsChoice`) and the panel's
 * quiet "Save without action" link saves as-is.
 *
 * @returns {{ label: string, quiet: boolean, needsChoice?: boolean }}
 */
export function resolveFoodSafetySubmitAffordance({
  outOfRange = false,
  corrective = '',
  saveFailed = false,
  actionOptional = false,
  withActionLabel = FOOD_SAFETY_SUBMIT_WITH_ACTION_LABEL,
} = {}) {
  if (saveFailed) {
    return { label: FOOD_SAFETY_SUBMIT_RETRY_LABEL, quiet: false }
  }
  const chosen = Boolean(String(corrective || '').trim())
  if (outOfRange && actionOptional) {
    return chosen
      ? { label: withActionLabel, quiet: false }
      : { label: withActionLabel, quiet: false, needsChoice: true }
  }
  if (outOfRange && !chosen) {
    return { label: FOOD_SAFETY_SUBMIT_ADD_NOTE_LABEL, quiet: true }
  }
  return { label: FOOD_SAFETY_SUBMIT_LABEL, quiet: false }
}

/**
 * Network / offline-shaped failures (no HTTP response).
 */
export function isLikelyFoodSafetyOfflineError(error) {
  if (error?.response) return false
  const message = String(error?.message || error?.code || '').toLowerCase()
  return (
    message.includes('network') ||
    message.includes('offline') ||
    message.includes('failed to fetch') ||
    message.includes('network request failed') ||
    message.includes('timeout') ||
    error?.name === 'NetworkError'
  )
}

/**
 * Chef-facing copy for a failed log submit. Draft must stay; Retry re-sends.
 */
export function formatFoodSafetySubmitError(error) {
  if (isLikelyFoodSafetyOfflineError(error)) {
    return OFFLINE_SUBMIT_MESSAGE
  }
  const apiMessage = String(
    error?.response?.data?.message || error?.message || '',
  ).trim()
  if (apiMessage && !/^network request failed$/i.test(apiMessage)) {
    if (/draft kept|retry/i.test(apiMessage)) return apiMessage
    return `${apiMessage} — draft kept. Tap Retry to try again.`
  }
  return FAILED_SUBMIT_FALLBACK
}
